import { useQuery, useQueryClient } from "@tanstack/react-query"
import BaseHeadBanner from "../../../components/elements/BaseHeadBanner"
import BasePage from "../../../components/layout/BasePage"
import { getEmployeeByIdData, getUserGroupsFromKC } from "../api/apiCalls"
import { useParams } from "react-router-dom"
import EmployeeDetailPart from "../components/EmployeeDetailPart"
import ErrorPage from "../../404/ErrorPage"
import { useContext, useMemo, useState } from "react"
import { Employee } from "../types/Types"
import { useSendInviteMutation, useUpdateEmployeeKcGroupMutation, useUpdateEmployeeMutation } from "../api/services"
import BaseGrowBody from "../../../components/elements/BaseGrowBody"
import { API, GeneralQueryKeys } from "../../../api/constants"
import { ModalContext } from "../../../components/contexts/ModalContext"
import { SidePanelContext } from "../../../components/contexts/SidePanelContext"
import BasePreview from "../../../components/elements/BasePreview"
import { ReactComponent as CALENDAR } from "../../../assets/CalendarICON.svg"
import { ReactComponent as TRUCK } from "../../../assets/Truck.svg"
import { ReactComponent as CLOCK } from "../../../assets/Clock.svg"
import { ReactComponent as FOLDER } from "../../../assets/Folder.svg"
import { HashLink } from "react-router-hash-link"
import { messageError } from "../../../services/messages"
import { getCostCenterNames, getEmployeeStatistics } from "../../Dashboard/api/apiCalls"
import EmployeeStatistics from "../components/EmployeeStatistics"
import Loading from "../../../components/common/Loading"
import BaseButton from "../../../components/elements/BaseButton"
import EmployeeDriversLicenceModal from "../components/EmployeeDriversLicenceModal"
import TimetrackingModal from "../components/TimetrackingModal"
import { processAbsencesOfEmployee } from "../../../services/functions"
import BaseFolderStructurModal from "../../../components/elements/BaseFolderStructurModal"
import SetPasswordModal from "../components/SetPasswordModal"

export default function MitarbeiterDetailPage() {
    const { id } = useParams()
    const modalCon = useContext(ModalContext)
    const sidePanelCon = useContext(SidePanelContext)

    // GET ALL User Groups (Rollen)
    const {
        data: kcData,
        error: kcError,
        isLoading: kcIsLoading,
    } = useQuery({
        queryKey: [GeneralQueryKeys.KC_USER_GROUPS],
        queryFn: getUserGroupsFromKC,
    })

    // get single employee by id
    const queryKeySingleEmployee = GeneralQueryKeys.EMPLOYEE_SINGLE + id
    const {
        error: singleEmployeeError,
        data: singleEmployeeData,
        isLoading: singleEmployeeIsLoading,
    } = useQuery({
        queryKey: [queryKeySingleEmployee],
        queryFn: () => getEmployeeByIdData(id ?? ""),
        enabled: !!id,
    })
    const displayedEmployee: Employee = useMemo(() => singleEmployeeData, [singleEmployeeData])
    const [updatedEmployee, setupdatedEmployee] = useState<Employee>()

    // GET ALL Cost Center Query
    const {
        error: costcenterError,
        data: costcenterData,
        isLoading: costcenterisLoading,
    } = useQuery({
        queryKey: [GeneralQueryKeys.COSTCENTER_NAMES],
        queryFn: getCostCenterNames,
    })

    // STATISTICS OF EMPLOYEE
    const { error: errorEmployeeStatistics, data: employeeStatistics } = useQuery({
        queryKey: [GeneralQueryKeys.EMPLOYEE_STATISTICS + id],
        queryFn: () => getEmployeeStatistics(id || ""),
    })

    // Einladung senden (Link zum Festlegen des Passworts)
    const queryClient = useQueryClient()
    const { mutate: sendInviteMail } = useSendInviteMutation(queryClient, id ?? "")
    const sendInvite = () => {
        sendInviteMail(displayedEmployee.kcId ?? "")
    }

    // UPDATE EMPLOYEE
    const { mutate: updateEmployee } = useUpdateEmployeeMutation(queryClient, id ?? "", queryKeySingleEmployee)
    const { mutate: updateEmployeeKcGroup } = useUpdateEmployeeKcGroupMutation(queryClient, id ?? "", queryKeySingleEmployee)
    const handleUpdateEmployee = () => {
        if (Object.keys(updatedEmployee as Employee).length > 1 || (Object.keys(updatedEmployee as Employee).length === 1 && Object.keys(updatedEmployee as Employee)[0] !== "kcId")) {
            if (!updatedEmployee?.kostenstelle || (updatedEmployee?.kostenstelle && Array.isArray(updatedEmployee.kostenstelle) && updatedEmployee.kostenstelle.length > 0)) {
                updateEmployee({
                    id: displayedEmployee._id ?? "",
                    body: {
                        ...updatedEmployee,
                        address: {
                            street: updatedEmployee?.address?.street ?? displayedEmployee?.address?.street ?? "",
                            nr: updatedEmployee?.address?.nr ?? displayedEmployee?.address?.nr ?? "",
                            plz: updatedEmployee?.address?.plz ?? displayedEmployee?.address?.plz ?? "",
                            city: updatedEmployee?.address?.city ?? displayedEmployee?.address?.city ?? "",
                        },
                    } as Employee,
                })
            } else {
                messageError("abc", "Aktualisieren Fehlgeschlagen!", "Bitte eine Niederlassung auswählen!")
            }
            if (updatedEmployee?.kcId && updatedEmployee?.kcId !== displayedEmployee.kcId) {
                updateEmployeeKcGroup({
                    kcid: displayedEmployee.kcId ?? singleEmployeeData.kcId ?? "",
                    gid: kcData.rbac.find((item: { name: string; id: any }) => item.name === updatedEmployee.kcId).id,
                })
            }
        }
        if (Object.keys(updatedEmployee as Employee).length === 1 && Object.keys(updatedEmployee as Employee)[0] === "kcId") {
            updateEmployeeKcGroup({
                kcid: displayedEmployee.kcId ?? singleEmployeeData.kcId ?? "",
                gid: kcData.rbac.find((item: { name: string; id: any }) => item.name === updatedEmployee?.kcId).id,
            })
        }
        setupdatedEmployee(undefined)
    }

    // get preview of pdf file
    const [selectedS3Key, setselectedS3Key] = useState<string>()
    const handlePreviewDocument = (s3Key: string) => {
        setselectedS3Key(s3Key)
        sidePanelCon?.trigger(1)
    }
    const employeeDownloadUrl = useMemo(() => {
        return singleEmployeeError || singleEmployeeIsLoading || !selectedS3Key ? undefined : API + `/employee/${id}/download?s3Key=${selectedS3Key}`
    }, [id, selectedS3Key, singleEmployeeError, singleEmployeeIsLoading])

    return (
        <BasePage>
            <BaseHeadBanner
                title={displayedEmployee?.first_name + " " + displayedEmployee?.last_name}
                button={
                    <HashLink className="btn btn-primary" to={"/kalender/" + displayedEmployee?._id}>
                        <CALENDAR className="w-6 h-6" />
                    </HashLink>
                }
            />
            {/* Detailed Information */}
            {(singleEmployeeIsLoading || kcIsLoading || costcenterisLoading) && <Loading classProps="absolute top-0 left-0 z-50 bg-white/40" />}
            {(singleEmployeeError || kcError || costcenterError || errorEmployeeStatistics) && <ErrorPage />}
            {singleEmployeeData && !singleEmployeeError && !singleEmployeeIsLoading && costcenterData && (
                <BaseGrowBody classProps="py-4 flex gap-4 lg:flex-row flex-col overflow-x-hidden grow overflow-y-scroll lg:overflow-y-hidden">
                    <EmployeeDetailPart
                        updatedEmployee={updatedEmployee}
                        setupdatedEmployee={setupdatedEmployee}
                        resetDataToOriginal={() => setupdatedEmployee(undefined)}
                        displayedEmployee={displayedEmployee}
                        sendInvite={sendInvite}
                        openSetPassword={() => modalCon?.trigger(4)}
                        kcGroups={kcData}
                        costcenter={costcenterData}
                        handleUpdateEmployee={handleUpdateEmployee}
                    />
                    <div className="flex flex-col gap-4 w-full grow ">
                        <div className="flex justify-center gap-2 sm:justify-end w-full pb-4">
                            <BaseButton hideLabelOnMobile="sm" label={"Führerschein"} func={() => modalCon?.trigger(1)} icon={<TRUCK className="w-6 h-6" />} classProps="w-2/5 sm:w-fit" />
                            <BaseButton hideLabelOnMobile="sm" label={"Dokumente"} func={() => modalCon?.trigger(3)} icon={<FOLDER className="w-6 h-6" />} classProps="w-2/5 sm:w-fit" />
                            <BaseButton hideLabelOnMobile="sm" label={"Zeiterfassung"} func={() => modalCon?.trigger(2)} icon={<CLOCK className="w-6 h-6" />} classProps="w-2/5 sm:w-fit" />
                        </div>
                        <EmployeeStatistics
                            statistics={employeeStatistics}
                            vacationContingent={displayedEmployee?.vacationContingentPerYear ?? 0}
                            absenceStatistics={processAbsencesOfEmployee(displayedEmployee?.absences ?? [])}
                        />
                    </div>
                </BaseGrowBody>
            )}
            <BasePreview title={"Dokumentvorschau"} sidePanelId={1} previewType={"document"} content={employeeDownloadUrl} />
            <EmployeeDriversLicenceModal modalId={1} id={id ?? ""} queryKey={queryKeySingleEmployee} driverslicence={displayedEmployee?.driversLicence} />
            <TimetrackingModal modalId={2} id={id ?? ""} />
            <SetPasswordModal modalId={4} kcid={displayedEmployee?.kcId ?? ""} employeeName={displayedEmployee?.first_name + " " + displayedEmployee?.last_name} />
            <BaseFolderStructurModal
                modalId={3}
                title={displayedEmployee?.first_name + " " + displayedEmployee?.last_name}
                type="employee"
                data={displayedEmployee?.folders ?? []}
                id={displayedEmployee?._id ?? ""}
                queryKey={queryKeySingleEmployee}
                documents={displayedEmployee?.s3Documents ?? []}
                handlePreviewDocument={handlePreviewDocument}
            />
        </BasePage>
    )
}
